import { Link, useParams, Navigate } from "react-router-dom";
import {
    ArrowUpRight, ArrowLeft, Check, ShieldCheck, Users, Sparkles, BarChart3, BookOpen,
} from "lucide-react";

import PageMeta from "../components/layout/PageMeta";
import FinalCTA from "../components/sections/FinalCTA";
import MiniContactCard from "../components/sections/MiniContactCard";
import { INDUSTRIES, getIndustry } from "../data/industries";

export default function IndustryDetail() {
    const { slug } = useParams();
    const industry = getIndustry(slug);

    if (!industry) return <Navigate to="/industries" replace />;

    const related = (industry.related || [])
        .map((s) => getIndustry(s))
        .filter(Boolean);

    return (
        <div className="pqx-page-enter" data-testid={`industry-detail-${industry.slug}`}>
            <PageMeta
                title={industry.name}
                description={industry.intro}
            />

            {/* Hero */}
            <section className="relative pt-28 md:pt-36 pb-12 md:pb-16 overflow-hidden pqx-dark-panel">
                <div className="absolute inset-0 pqx-grid-bg-dark opacity-50" />
                <div
                    className="absolute right-[-10%] top-[20%] w-[420px] h-[420px] rounded-full pointer-events-none blur-3xl opacity-50"
                    style={{ background: "radial-gradient(circle, rgba(255,64,64,0.20), transparent 60%)" }}
                />
                <div className="pqx-container relative z-10 max-w-5xl">
                    <Link
                        to="/industries"
                        data-testid="industry-back"
                        className="inline-flex items-center gap-2 text-sm text-white/65 hover:text-white pqx-reveal"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        All industries
                    </Link>
                    <span className="pqx-eyebrow !text-[#FF4040] mt-6 inline-flex pqx-reveal pqx-delay-1">
                        Industry · {industry.short}
                    </span>
                    <h1 className="pqx-h1 mt-5 text-white pqx-reveal pqx-delay-2">
                        {industry.name}
                    </h1>
                    <p className="pqx-display mt-4 text-lg md:text-2xl text-[#FF4040] leading-snug max-w-3xl pqx-reveal pqx-delay-3">
                        {industry.tagline}
                    </p>
                    <p className="mt-5 md:mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-3xl pqx-reveal pqx-delay-3">
                        {industry.intro}
                    </p>

                    <div className="mt-10 grid sm:grid-cols-3 gap-4 pqx-reveal pqx-delay-4">
                        {industry.kpis.map((k) => (
                            <div
                                key={k.label}
                                className="pqx-glass-dark rounded-xl p-4"
                            >
                                <p className="pqx-display text-2xl text-[#FF4040]">
                                    {k.value}
                                </p>
                                <p className="text-xs uppercase tracking-[0.16em] text-white/55 mt-1">
                                    {k.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Content + sidebar */}
            <section className="bg-[#FAFAF8] py-20" data-testid="industry-body">
                <div className="pqx-container grid lg:grid-cols-[1.5fr_1fr] gap-10">
                    {/* Main content */}
                    <div className="space-y-12">
                        {/* Challenges */}
                        <div>
                            <span className="pqx-eyebrow">Business challenges</span>
                            <h2 className="pqx-display mt-4 text-2xl md:text-4xl text-[#303030]">
                                Where the friction lives.
                            </h2>
                            <ul className="mt-6 space-y-3">
                                {industry.challenges.map((c, i) => (
                                    <li
                                        key={i}
                                        data-testid={`industry-challenge-${i}`}
                                        className="flex items-start gap-3 text-[15px] text-[#303030]/85 leading-relaxed"
                                    >
                                        <span className="mt-1 w-6 h-6 grid place-items-center rounded-full bg-[#FF4040]/12 text-[#FF4040] shrink-0 text-xs font-semibold">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        {c}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Use cases */}
                        <div>
                            <span className="pqx-eyebrow">AI & engineering use cases</span>
                            <h2 className="pqx-display mt-4 text-2xl md:text-4xl text-[#303030]">
                                Patterns we ship every quarter.
                            </h2>
                            <div className="mt-6 grid sm:grid-cols-2 gap-4">
                                {industry.useCases.map((u, i) => (
                                    <div
                                        key={u.title}
                                        data-testid={`industry-usecase-${i}`}
                                        className="pqx-card-elevated !p-5"
                                    >
                                        <span className="w-9 h-9 grid place-items-center rounded-xl bg-[#303030]/5 text-[#303030]">
                                            <Sparkles className="w-4 h-4" />
                                        </span>
                                        <h3 className="pqx-display text-lg text-[#303030] mt-4">
                                            {u.title}
                                        </h3>
                                        <p className="mt-2 text-sm text-[#303030]/70 leading-relaxed">
                                            {u.copy}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Case study */}
                        <div
                            className="rounded-3xl bg-[#191919] text-[#FAFAF8] p-8 md:p-10 relative overflow-hidden"
                            data-testid="industry-case-study"
                        >
                            <div className="absolute inset-0 pqx-grid-bg-dark opacity-40" />
                            <div className="relative">
                                <span className="pqx-eyebrow !text-[#FF4040] inline-flex items-center gap-2">
                                    <BookOpen className="w-3.5 h-3.5" />
                                    Case study
                                </span>
                                <h2 className="pqx-display text-2xl md:text-3xl mt-5">
                                    {industry.caseStudy.title}
                                </h2>

                                <div className="grid sm:grid-cols-3 gap-4 mt-7">
                                    {industry.caseStudy.metrics.map((m) => (
                                        <div
                                            key={m.label}
                                            className="pqx-glass-dark rounded-xl p-4"
                                        >
                                            <p className="pqx-display text-2xl text-[#FF4040]">
                                                {m.value}
                                            </p>
                                            <p className="text-xs uppercase tracking-[0.14em] text-white/55 mt-1">
                                                {m.label}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-8 space-y-5 text-white/80 leading-relaxed">
                                    <div>
                                        <p className="text-xs uppercase tracking-[0.18em] text-white/50">
                                            Challenge
                                        </p>
                                        <p className="mt-2 text-[15px]">
                                            {industry.caseStudy.challenge}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs uppercase tracking-[0.18em] text-white/50">
                                            Approach
                                        </p>
                                        <p className="mt-2 text-[15px]">
                                            {industry.caseStudy.approach}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs uppercase tracking-[0.18em] text-white/50">
                                            Outcome
                                        </p>
                                        <p className="mt-2 text-[15px]">
                                            {industry.caseStudy.outcome}
                                        </p>
                                    </div>
                                </div>

                                <p className="mt-7 text-xs text-white/45">
                                    Case study details are anonymised to respect client confidentiality.
                                </p>
                            </div>
                        </div>

                        {/* Staffing support */}
                        <div className="pqx-card-elevated !p-8">
                            <span className="pqx-eyebrow inline-flex items-center gap-2">
                                <Users className="w-3.5 h-3.5" />
                                Staffing & consulting support
                            </span>
                            <h2 className="pqx-display text-2xl md:text-3xl text-[#303030] mt-4">
                                Talent built for {industry.name.split(" & ")[0]} programs.
                            </h2>
                            <p className="mt-4 text-[15px] text-[#303030]/75 leading-relaxed">
                                {industry.talent}
                            </p>
                            <div className="mt-6 flex flex-wrap gap-3">
                                <Link
                                    to="/talent-solutions"
                                    data-testid="industry-talent-link"
                                    className="pqx-btn-primary"
                                >
                                    Explore talent services
                                    <ArrowUpRight className="w-4 h-4" />
                                </Link>
                                <Link
                                    to="/ai-solutions"
                                    className="pqx-btn-ghost"
                                >
                                    AI services for {industry.name.split(" & ")[0]}
                                </Link>
                            </div>
                        </div>

                        {/* Related industries */}
                        {related.length > 0 && (
                            <div>
                                <span className="pqx-eyebrow">Related industries</span>
                                <h2 className="pqx-display mt-4 text-2xl text-[#303030]">
                                    You might also explore.
                                </h2>
                                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                                    {related.map((r) => (
                                        <Link
                                            key={r.slug}
                                            to={`/industries/${r.slug}`}
                                            data-testid={`industry-related-${r.slug}`}
                                            className="pqx-card-elevated !p-5 flex items-start gap-4 group"
                                        >
                                            <span className="w-10 h-10 grid place-items-center rounded-xl bg-[#FF4040]/12 text-[#FF4040] shrink-0">
                                                <Sparkles className="w-4 h-4" />
                                            </span>
                                            <div className="flex-1">
                                                <p className="pqx-display text-base text-[#303030] group-hover:text-[#FF4040] transition-colors">
                                                    {r.name}
                                                </p>
                                                <p className="text-xs text-[#303030]/55 mt-1">
                                                    {r.short}
                                                </p>
                                            </div>
                                            <ArrowUpRight className="w-4 h-4 text-[#303030]/40 group-hover:text-[#FF4040]" />
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <aside className="lg:pl-2">
                        <MiniContactCard
                            serviceInterest={industry.name}
                            eyebrow={`Talk about ${industry.name.split(" & ")[0]}`}
                            heading={`Bring AI to your ${industry.name.split(" & ")[0]} program.`}
                            description="Tell us a little about your priorities. We'll route your enquiry to the team lead closest to your industry."
                            testidPrefix={`industry-contact-${industry.slug}`}
                        />
                    </aside>
                </div>
            </section>

            <FinalCTA
                title={`Ready to bring AI inside ${industry.name.split(" & ")[0]}?`}
                description="We'll respond with a sequenced, ROI-mapped plan tailored to your industry — within one business day."
                primary={{ to: "/contact", label: "Start a conversation" }}
                secondary={{ to: "/industries", label: "All industries" }}
            />
        </div>
    );
}
